import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from './SEO';
import { FileText, CheckCircle, Shield, Building2, Users, AlertTriangle } from 'lucide-react';

export default function PolicyKitPage() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    siteType: '',
    teamSize: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Netlify will handle the form submission via the hidden form in index.html
    const form = e.target as HTMLFormElement;
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(new FormData(form) as any).toString()
    })
      .then(() => navigate('/shift-distraction-policy-kit/thanks'))
      .catch((error) => alert('Error submitting form. Please try again.'));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Workplace Phone Distraction Policy Kit | Nix Spaces"
        description="Get leadership buy-in for your warehouse or industrial phone policy. Ready-to-adapt policy template, phased rollout plan, and employee acknowledgment forms for EHS and safety managers."
        canonical="https://nixspaces.com/shift-distraction-policy-kit"
      />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <a href="/" className="flex items-center space-x-3">
                <img src="/image.png" alt="Nix Logo" className="h-12 w-auto mt-1" />
                <span className="text-2xl font-bold text-gray-900">Spaces</span>
              </a>
            </div>
            <nav className="hidden md:flex space-x-6">
              <a href="/" className="text-gray-600 hover:text-blue-600 transition-colors">Home</a>
              <a href="/#contact" className="text-gray-600 hover:text-blue-600 transition-colors">Contact</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-cyan-500 mb-6">
            <FileText className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get Leadership Buy-In for Your Phone Policy
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            A ready-to-adapt worksite distraction policy kit for warehouse, industrial, and field operations
          </p>
          <p className="text-lg text-gray-500">
            Put this in front of ops, legal, and leadership — cover yourself with a clear, defensible policy
          </p>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What's in the Kit</h2>
          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 mb-8">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-cyan-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Sample Worksite Phone & Device Policy</h3>
                  <p className="text-gray-600 text-sm">Complete policy template with clear rules for operational areas</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-cyan-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Manager Rollout Checklist</h3>
                  <p className="text-gray-600 text-sm">4-week implementation plan for site leaders</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-cyan-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Employee FAQ</h3>
                  <p className="text-gray-600 text-sm">Common questions and answers for your team</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-cyan-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Employee Acknowledgment Form</h3>
                  <p className="text-gray-600 text-sm">Ready-to-use signature template</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-cyan-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Optional Monitoring Tools Guide</h3>
                  <p className="text-gray-600 text-sm">How technology supports policy compliance</p>
                </div>
              </div>
            </div>
          </div>

          {/* Why Download This Kit */}
          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Why EHS & Safety Managers Use This Kit</h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-cyan-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Something leadership can review and approve</h4>
                  <p className="text-gray-600 text-sm">A complete, professional document you can circulate to ops managers, HR, and legal for sign-off before rollout</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-cyan-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Phased implementation roadmap included</h4>
                  <p className="text-gray-600 text-sm">4-week rollout checklist with communication templates and training milestones — not just policy text</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-cyan-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Employee acknowledgment forms ready to use</h4>
                  <p className="text-gray-600 text-sm">Documented acceptance showing workers were informed — critical for enforcement and liability protection</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-cyan-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Optional path to visibility and compliance tracking</h4>
                  <p className="text-gray-600 text-sm">Once your policy is in place, Nix Spaces provides monitoring and logs to show the policy is being followed</p>
                </div>
              </div>
            </div>
          </div>

          {/* Important Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-12">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-2">Important Legal Notice</h3>
                <p className="text-amber-800 text-sm mb-2">
                  This kit is a <strong>practical template for employer customization</strong> and is <strong>not legal advice</strong>. It is not a substitute for review by your employment counsel, safety counsel, or HR team.
                </p>
                <p className="text-amber-800 text-sm">
                  <strong>Section 1.8 (Notice of Monitoring)</strong> is an explicit <strong>FILL IN WITH COUNSEL</strong> placeholder. Laws on workplace monitoring, employee notice, and privacy vary by state and country. You must complete this section with your legal counsel before implementing any monitoring program.
                </p>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Get the Shift Distraction Policy Kit</h2>
              <p className="text-gray-600">Start building your case for a safer, more focused worksite</p>
            </div>

            <form 
              name="policy-kit"
              method="POST"
              data-netlify="true"
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <input type="hidden" name="form-name" value="policy-kit" />

              {/* Contact Information */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Work Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                  placeholder="Your Company"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="siteType" className="block text-sm font-medium text-gray-700 mb-2">
                    Site Type *
                  </label>
                  <select
                    id="siteType"
                    name="siteType"
                    value={formData.siteType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="">Select site type</option>
                    <option value="Warehouse">Warehouse</option>
                    <option value="Industrial">Industrial</option>
                    <option value="Field">Field Operations</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="teamSize" className="block text-sm font-medium text-gray-700 mb-2">
                    Team Size *
                  </label>
                  <select
                    id="teamSize"
                    name="teamSize"
                    value={formData.teamSize}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="">Select team size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="200+">200+ employees</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                  placeholder="Any specific questions or context about your needs..."
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-center pt-4">
                <button
                  type="submit"
                  className="bg-cyan-500 text-white px-12 py-4 rounded-2xl text-lg font-semibold hover:bg-cyan-600 transition-all duration-300 flex items-center space-x-2 shadow-lg hover:shadow-xl"
                >
                  <FileText className="h-5 w-5" />
                  <span>Download Policy Kit</span>
                </button>
              </div>
            </form>
          </div>

          {/* Who This Is For */}
          <div className="mt-12 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-3xl p-8 border border-cyan-100">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">Perfect for EHS & Safety Leaders in:</h3>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="bg-cyan-500 w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Building2 className="h-6 w-6 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Warehouses</h4>
                <p className="text-gray-600 text-sm">Distribution centers, fulfillment facilities, and logistics operations</p>
              </div>
              <div>
                <div className="bg-emerald-500 w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Industrial Sites</h4>
                <p className="text-gray-600 text-sm">Manufacturing plants, yards, and high-risk production floors</p>
              </div>
              <div>
                <div className="bg-violet-500 w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Field Operations</h4>
                <p className="text-gray-600 text-sm">Construction, utilities, and mobile workforce teams</p>
              </div>
            </div>
          </div>

          {/* Soft Product Bridge */}
          <div className="mt-12 bg-gradient-to-br from-slate-50 to-gray-100 rounded-3xl p-8 border border-gray-200">
            <div className="text-center">
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">After Your Policy Is in Place</h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Nix Spaces helps you prove the policy is working — visibility logs, manager alerts, and compliance documentation for OSHA, audits, and leadership reviews.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/"
                  className="inline-flex items-center justify-center space-x-2 border-2 border-cyan-500 text-cyan-500 px-6 py-3 rounded-2xl font-semibold hover:bg-cyan-500 hover:text-white transition-all duration-300"
                >
                  <span>Learn About Nix Spaces</span>
                </a>
                <a
                  href="/#contact"
                  className="inline-flex items-center justify-center space-x-2 text-gray-700 px-6 py-3 rounded-2xl font-semibold hover:text-cyan-600 transition-all duration-300"
                >
                  <span>Schedule a Demo</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">© 2024 Nix Spaces. Template provided for educational and operational planning purposes.</p>
        </div>
      </footer>
    </div>
  );
}
